import { useEffect, useRef, type RefObject } from 'react'

/**
 * What a dialog owes a keyboard, without owning its markup (D-515).
 *
 * <b>`Modal` is the shape; this is the behaviour.</b> Five of the app's twelve
 * overlays are 700–1500-line components whose panel is a `<form>`, or which
 * carry their own scrolling and a nested dialog — `BookingForm`, `RosterModal`,
 * the `ClassRecordView` drawer, `DeclareOutDay`, `PrivateRequestsPanel`. Rewriting
 * their JSX to sit inside a shell is a real risk to a teacher's booking flow
 * for no visible gain, and this sweep's whole promise is that nothing visible
 * changes.
 *
 * So the trap, the scroll lock and the Escape handling come out as a hook they
 * can call while keeping every tag exactly where it is. `Modal` calls it too,
 * so there is still one implementation rather than two.
 *
 * <b>Escape is consumed, and only the topmost dialog answers it.</b> Keydown
 * goes `document` → `window`, and at least one screen closes itself on Escape
 * at the window — so a dialog that merely listened would dismiss itself *and*
 * the page behind it, discarding whatever was unsaved there. That is not
 * hypothetical: it shipped for one review round on `ClassRecordView`.
 */

/**
 * Elements a keyboard can reach, in document order.
 *
 * Two bounds worth naming, both currently unhit and both silent if they ever
 * are. The filter below tests the `hidden` **attribute**, not Tailwind's
 * `hidden` **class** — a control hidden by `display: none` from a class would
 * be trapped into and invisible. And an `aria-hidden` subtree is not excluded.
 * Neither exists in any dialog on the shell today; a new one that needs either
 * should extend this rather than write its own trap.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/*
 * Every open dialog, innermost last — so Escape and the Tab trap belong to the
 * one the person is actually in, rather than to all of them at once.
 */
const open: symbol[] = []

/*
 * Captured once, when the first dialog opens. Captured per dialog, two open at
 * a time meant the second read the first's `hidden` and restored it faithfully,
 * leaving the page unscrollable for good.
 */
let restoreOverflow: string | null = null

export function useModalShell({
  panel,
  onDismiss,
  active = true,
}: {
  /** The dialog's own panel — whatever element the caller already renders. */
  panel: RefObject<HTMLElement | null>
  /** Called for Escape. Omit for a dialog that must be answered. */
  onDismiss?: () => void
  /**
   * Whether the dialog is on screen.
   *
   * Most callers are mounted only while open and can leave this alone. Some
   * hold the dialog behind their own flag in the same component — a hook cannot
   * be called conditionally, so they pass the flag instead of restructuring,
   * which is the whole point of this existing separately from `Modal`.
   */
  active?: boolean
}) {
  /*
   * Whoever had focus before this rendered, read during render rather than in
   * the effect: React applies a child's `autoFocus` during commit, which is
   * before the effect, so by then the active element may already be a field
   * inside this dialog — and "restoring" to that would put focus on something
   * about to unmount.
   *
   * Read on the render that *opens* it, not on the caller's first render. A
   * dialog held behind its owner's own flag renders closed first, so capturing
   * unconditionally records whatever had focus when the screen behind it
   * mounted — usually nothing. The case that made this matter is a confirmation
   * nested inside another dialog: it would have sent focus back past its own
   * parent to whatever opened *that*, which is out of the panel entirely.
   *
   * Keyed on the closed → open *transition* rather than on the ref still being
   * empty, so that a second opening reads its opener afresh. Clearing it in the
   * cleanup is the obvious way to do the same thing and is wrong in
   * `npm run dev`: StrictMode double-invokes an effect on mount — create,
   * destroy, create — and the capture happens during render, which by then is
   * over, so the ref would be empty for the dialog's whole life and closing
   * would restore focus to nothing.
   *
   * <b>On mount, not on a later dependency change</b>, which is why the test
   * that catches it has to be one whose dialog is mounted already open. A
   * dialog held behind its owner's flag never sees the double-invoke at all.
   *
   * `wasActive` is the whole guard; the `active &&` beside it only avoids
   * re-reading `document.activeElement` on every render of the screen behind a
   * closed dialog. Removing it changes no outcome and no test goes red, which
   * is worth saying rather than leaving a reader to assume both halves matter.
   */
  const opener = useRef<HTMLElement | null>(null)
  const wasActive = useRef(false)

  if (active && !wasActive.current) opener.current = document.activeElement as HTMLElement | null
  wasActive.current = active

  // Held in a ref so the effect registers once: a dependency on the handler
  // would re-register on every render of the screen behind the dialog.
  const dismiss = useRef(onDismiss)
  dismiss.current = onDismiss

  useEffect(() => {
    if (!active) return

    const me = Symbol('modal')

    if (open.length === 0) {
      restoreOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }

    /*
     * Only if the caller has not already placed it. Asked as "is focus already
     * inside me" rather than by looking for `autofocus`, because React does not
     * leave that attribute in the DOM — it focuses the node itself.
     */
    if (!panel.current?.contains(document.activeElement)) panel.current?.focus()

    /*
     * Joined after the focus call, which is the part most likely to throw.
     * React discards an effect that throws and never registers its cleanup — so
     * a symbol pushed first and left behind would sit on top of the stack for
     * ever, and every dialog opened afterwards would decide it was not the
     * topmost and stop answering Escape until the page was reloaded.
     *
     * The scroll lock above is deliberately *not* covered by this, and cannot
     * be: something has to take it before the focus moves. It has the same
     * shape of hazard — a throw between the two would leave `restoreOverflow`
     * holding `'hidden'`, so the next dialog would faithfully restore an
     * unscrollable page. Nothing in that window can realistically throw today,
     * which is why this is written down rather than defended against.
     */
    open.push(me)

    const onKey = (e: KeyboardEvent) => {
      // Only the dialog on top answers. Anything underneath is covered.
      if (open[open.length - 1] !== me) return

      if (e.key === 'Escape') {
        e.stopPropagation()
        dismiss.current?.()
        return
      }

      if (e.key !== 'Tab' || !panel.current) return

      /*
       * Not filtered on `offsetParent`: that depends on layout, which jsdom
       * does not do, and it is the wrong question anyway — a control hidden
       * from the tab order is hidden by `hidden` or `disabled`, which the
       * selector already asks about.
       */
      const reachable = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
        .filter((el) => !el.closest('[hidden]'))

      // Nothing to move between, so hold the panel rather than letting the page
      // behind take the focus.
      if (reachable.length === 0) {
        e.preventDefault()
        panel.current.focus()
        return
      }

      const first = reachable[0]
      const last = reachable[reachable.length - 1]
      const on = document.activeElement

      if (e.shiftKey && (on === first || on === panel.current)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && on === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey)

    return () => {
      document.removeEventListener('keydown', onKey)

      const at = open.indexOf(me)
      if (at >= 0) open.splice(at, 1)

      if (open.length === 0 && restoreOverflow !== null) {
        document.body.style.overflow = restoreOverflow
        restoreOverflow = null
      }

      /*
       * Back where they were, not the top of the document. When the opener has
       * itself unmounted this is a silent no-op and focus falls to the body —
       * the promise degrades rather than breaking.
       */
      opener.current?.focus?.()
    }
  }, [panel, active])
}
